import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import AddIcon from '@mui/icons-material/Add';
import useMediaQuery from '@mui/material/useMediaQuery';

import Button from 'shared/atoms/Button';
import CircularSpinner from 'shared/atoms/CircularSpinner';
import T from 'shared/atoms/Typography';
import Dialog from 'shared/molecules/Dialog';

import theme from 'shared/themes/default';
import { newSelectTranslation } from 'shared/utils';

import PlaylistContentItemList from './components/PlaylistContentItemList';
import { usePlaylist } from '../../../../context';

const StyledCourses = styled.div`
  flex: 50%;
  max-width: 50%;
  padding-left: 1.375rem;
  text-align: center;
`;

const StyledPlaylistContentTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  min-height: 40px;
`;

const StyledPlaylistTitleBlock = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 20px;
  min-height: 40px;
  ${theme.breakpoints.tablet} {
    flex-direction: column;
    align-items: flex-start;
    min-height: 90px;
  }
  ${theme.breakpoints.mobile} {
    min-height: 72px;
  }
`;

const PlaylistContent = () => {
  const isMobile = useMediaQuery(theme.breakpoints.mobile);
  const { t } = useTranslation();

  const {
    canManagePlaylists,
    hasSubscription,
    locale,
    dialogs: { addContentHandler, deleteFromPlaylist },
    playlists: {
      selectedPlaylist: playlist,
      selectPlaylistHandler,
      loadingPlaylists,
    },
  } = usePlaylist();

  const courseMenuItems = [
    {
      label: t('playlist.removeFromPlaylist'),
      handler: deleteFromPlaylist,
      disabled: !hasSubscription,
    },
  ];

  if (isMobile) {
    return (
      <Dialog
        ariaLabel={t('playlist.courses')}
        title={
          <>
            <T
              color={theme.palette.mainDark}
              fontWeight="medium"
              variant="paragraph"
            >
              {t('playlist.courses')}
            </T>
            <T
              color={theme.palette.mainDark}
              fontWeight="medium"
              variant="subtitle"
            >
              {newSelectTranslation(playlist.name, locale)}
            </T>
          </>
        }
        onClose={() => selectPlaylistHandler(null)}
      >
        {loadingPlaylists ? (
          <CircularSpinner static />
        ) : (
          <>
            {playlist.courses.length > 0 && canManagePlaylists && (
              <Button
                ariaLabel={t('playlist.addCourses')}
                disabled={!hasSubscription}
                fullWidth
                iconRight={AddIcon}
                onClick={() => addContentHandler()}
                sx={{
                  justifyContent: 'space-between',
                  marginBottom: theme.spacing.m1,
                }}
                theme="light"
                type="secondaryOutline"
              >
                {t('playlist.addCourses')}
              </Button>
            )}
            {playlist.courses.length > 0 ? (
              <PlaylistContentItemList
                menuItems={canManagePlaylists ? courseMenuItems : []}
                playlistContent={playlist.courses}
              />
            ) : (
              <T
                color={theme.palette.mainDark}
                fontWeight="medium"
                variant="paragraph"
              >
                {t('playlist.currentlyHaveNoCourses')}
              </T>
            )}
            {playlist.courses.length === 0 && (
              <Button
                ariaLabel={t('playlist.addCourses')}
                disabled={!hasSubscription}
                fullWidth
                iconRight={AddIcon}
                onClick={() => addContentHandler()}
                sx={{
                  justifyContent: 'space-between',
                  marginBottom: theme.spacing.m1,
                  marginTop: theme.spacing.m1,
                }}
                theme="light"
                type="primary"
              >
                {t('playlist.addCourses')}
              </Button>
            )}
          </>
        )}
      </Dialog>
    );
  }

  return (
    <StyledCourses>
      {loadingPlaylists ? (
        <CircularSpinner static />
      ) : (
        <>
          <StyledPlaylistTitleBlock>
            <StyledPlaylistContentTitleBlock>
              <T
                color={theme.palette.lightPure}
                fontWeight="medium"
                variant="helperText"
              >
                {t('playlist.courses')}
              </T>
              <T
                color={theme.palette.lightPure}
                fontWeight="medium"
                variant="paragraph"
              >
                {newSelectTranslation(playlist.name, locale)}
              </T>
            </StyledPlaylistContentTitleBlock>
            {playlist.courses.length > 0 && canManagePlaylists && (
              <Button
                ariaLabel={t('playlist.addCourses')}
                disabled={!hasSubscription}
                iconRight={AddIcon}
                onClick={() => addContentHandler()}
                sx={{
                  [theme.breakpoints.tablet]: {
                    width: '100%',
                    maxWidth: '300px',
                    justifyContent: 'space-between',
                  },
                }}
                theme="dark"
                type="secondaryOutline"
              >
                {t('playlist.addCourses')}
              </Button>
            )}
          </StyledPlaylistTitleBlock>
          {playlist.courses.length > 0 ? (
            <PlaylistContentItemList
              menuItems={canManagePlaylists ? courseMenuItems : []}
              playlistContent={playlist.courses}
            />
          ) : (
            <T
              align="center"
              color={theme.palette.disabledLight}
              fontWeight="medium"
              variant="paragraph"
            >
              {t('playlist.currentlyHaveNoCourses')}
            </T>
          )}
          {playlist.courses.length === 0 && (
            <Button
              ariaLabel={t('playlist.addCourses')}
              disabled={!hasSubscription}
              iconRight={AddIcon}
              onClick={() => addContentHandler()}
              sx={{
                width: '100%',
                margin: '10px 0',
                maxWidth: '300px',
                justifyContent: 'space-between',
              }}
              theme="dark"
              type="secondary"
            >
              {t('playlist.addCourses')}
            </Button>
          )}
        </>
      )}
    </StyledCourses>
  );
};

export default PlaylistContent;
